import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 shadow-xl",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow-[var(--shadow-premium)] hover:bg-primary/90 hover:shadow-[var(--shadow-glow)] hover:scale-105",
        glass: "bg-[var(--glass-bg)] backdrop-blur-md border-2 border-[var(--glass-border)] text-foreground hover:bg-[var(--glass-border)] hover:scale-105",
        outline: "border-2 border-[var(--stroke-strong)] bg-transparent text-foreground hover:bg-[var(--glass-bg)] hover:border-[var(--primary)] hover:scale-105",
        secondary: "bg-surface text-foreground shadow-[var(--shadow-premium)] hover:bg-muted border-2 border-[var(--glass-border)] hover:scale-105",
        ghost: "hover:bg-[var(--glass-bg)] hover:text-foreground hover:scale-105",
        link: "text-primary underline-offset-4 hover:underline hover:scale-105",
        destructive: "bg-destructive text-destructive-foreground shadow-[var(--shadow-premium)] hover:bg-destructive/90 hover:scale-105",
      },
      size: {
        default: "h-12 px-8 py-3 text-base",  /* Larger default size */
        sm: "h-10 rounded-lg px-6 text-sm",   /* Larger sm size */
        lg: "h-14 rounded-xl px-10 text-lg font-bold",  /* Much larger lg */
        xl: "h-16 rounded-2xl px-12 text-xl font-bold", /* Even larger xl */
        icon: "h-12 w-12",  /* Larger icon buttons */
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      >
        {children}
      </Comp>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
